import React from 'react';
import { FaTimes, FaLock, FaCrown, FaCheck } from 'react-icons/fa';
import '../../styles/RateLimitModal.scss';

const RateLimitModal = ({ 
  isOpen, 
  onClose, 
  feature, 
  usage, 
  message, 
  nextReset,
  upgradeRequired = true,
  userData = null,
  onSubmitToTeacher = null
}) => {
  if (!isOpen) return null;
  
  // Don't show rate limit modal to teachers - they have unlimited access
  if (userData?.role === 'teacher') return null;

  // Feature display names
  const featureDisplayNames = {
    'chat': 'AI Chat Messages',
    'math': 'Math Problem Solutions',
    'essay': 'Essay Analyses',
    'flashcard': 'Flashcard Sessions',
    'quiz': 'Practice Quizzes'
  };

  const featureDisplay = featureDisplayNames[feature] || feature;
  
  // Check if user can submit to teacher (only for essays with linked teacher)
  const canSubmitToTeacher = feature === 'essay' && 
                              userData?.role === 'student' && 
                              userData?.teacher_email && 
                              onSubmitToTeacher;

  // Feature-specific upgrade messaging
  const featureUpgradeMessages = {
    'flashcard': {
      title: "Don't stop now! You're on a roll 🔥",
      description: "Keep your study momentum going with unlimited flashcard sessions."
      
    },
    'essay': {
      title: "Keep improving your writing! ✍️",
      description: "Get unlimited feedback to perfect your essays."
      
    },
    'chat': {
      title: "Keep learning with AI! 🤖",
      description: "Get unlimited AI tutoring whenever you need help."
      
    },
    'quiz': {
      title: "Keep practicing! 📝",
      description: "Unlimited quizzes mean unlimited improvement."
    }
  };

  const upgradeMessage = featureUpgradeMessages[feature] || {
    title: "Keep going!",
    description: "Get unlimited access to all features.",
  };

  // Format next reset date
  const formatNextReset = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = date - now;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 0) {
        return { short: 'soon', long: 'soon' };
      } else if (diffDays === 1) {
        return { short: 'tomorrow', long: 'tomorrow' };
      } else {
        return { short: `in ${diffDays} days`, long: `${diffDays} days` };
      }
    } catch (e) {
      return { short: 'next Monday', long: 'next Monday' };
    }
  };

  const resetTime = nextReset ? formatNextReset(nextReset) : { short: 'next Monday', long: 'next Monday' };

  return (
    <div className="rate-limit-modal-overlay" onClick={(e) => {
      // Close modal if clicking on overlay (but not on modal content)
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="rate-limit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="limit-icon">
            <FaLock />
          </div>
          <h2>{upgradeMessage.title}</h2>
          <button onClick={onClose} className="close-button">
            <FaTimes />
          </button>
        </div>

        <div className="modal-content" style={{ padding: '0 24px 20px' }}>
          <div className="usage-summary" style={{ marginBottom: '16px' }}>
            {message ? (
              <p className="primary-message">
                {message}
              </p>
            ) : (
              <>
                <p className="primary-message">
                  You've used all <strong>{usage?.limit || 0}</strong> weekly {featureDisplay.toLowerCase()}.
                </p>
                <p className="reset-info">
                  Your limit resets {resetTime.short}.
                </p>
              </>
            )}
          </div>

          {upgradeRequired && (
            <>
              <div className="upgrade-prompt" style={{ marginBottom: '12px' }}>
                <div 
                  className="premium-badge"
                  onClick={() => {
                    window.location.href = '/subscribe';
                  }}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(243, 156, 18, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <FaCrown />
                  <span>Upgrade to Premium</span>
                </div>
                <h3 style={{ margin: '8px 0' }}>{upgradeMessage.description}</h3>
              </div>

              <div className="premium-benefits" style={{ 
                marginBottom: '16px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <h4 style={{ fontSize: '14px', marginBottom: '8px', marginTop: 0 }}>Premium includes:</h4>
                <ul style={{ 
                  margin: 0, 
                  padding: 0,
                  listStyle: 'none',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px',
                  fontSize: '12px'
                }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Unlimited essays
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Unlimited AI chat
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Unlimited math
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Unlimited quizzes
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Unlimited flashcards
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaCheck style={{ color: '#27ae60', fontSize: '12px' }} /> Priority support
                  </li>
                </ul>
              </div>

              <div className="action-buttons" style={{ 
                display: 'flex', 
                flexDirection: 'row',
                gap: '10px', 
                justifyContent: 'center',
                marginBottom: '12px'
              }}>
                <button 
                  className="upgrade-button"
                  onClick={() => {
                    // Navigate to subscription page
                    window.location.href = '/subscribe';
                  }}
                  style={{
                    flex: '1',
                    maxWidth: '195px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    background: 'linear-gradient(135deg, #3498db, #2980b9)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <FaCrown style={{ fontSize: '14px' }} />
                  Start Free Trial 
                </button>
                <button 
                  className="secondary-button"
                  onClick={onClose}
                  style={{
                    flex: '1',
                    maxWidth: '195px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    background: 'transparent',
                    border: '2px solid #ecf0f1',
                    borderRadius: '10px',
                    color: '#7f8c8d',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  I'll Wait {resetTime.long}
                </button>
              </div>

              {canSubmitToTeacher && (
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <button 
                    className="teacher-submit-button"
                    onClick={() => {
                      onClose();
                      onSubmitToTeacher();
                    }}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      maxWidth: '420px',
                      justifyContent: 'center'
                    }}
                  >
                    📝 Submit to Teacher for Review
                  </button>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#6b7280', 
                    marginTop: '6px',
                    marginBottom: 0
                  }}>
                    Your teacher will review the AI feedback before you can see it
                  </p>
                </div>
              )}

              <div className="trial-info" style={{ 
                marginTop: '8px',
                padding: '10px',
                background: 'linear-gradient(135deg, #e8f5e8, #d4edda)',
                borderRadius: '6px',
                borderLeft: '3px solid #27ae60'
              }}>
                <p style={{ fontSize: '12px', margin: 0 }}>
                  <strong>3-day free trial • No payment until trial ends • Cancel anytime</strong>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateLimitModal; 